import { useState, useEffect, useCallback } from 'react';

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
  action?: React.ReactNode;
}

interface ToastState {
  toasts: Toast[];
}

const toastState: ToastState = {
  toasts: [],
};

let listeners: Array<(state: ToastState) => void> = [];

function dispatch(toast: Toast) {
  toastState.toasts = [...toastState.toasts, toast];
  listeners.forEach((listener) => {
    listener(toastState);
  });

  // Auto-dismiss after 5 seconds
  setTimeout(() => {
    dismiss(toast.id);
  }, 5000);
}

function dismiss(toastId: string) {
  toastState.toasts = toastState.toasts.filter((t) => t.id !== toastId);
  listeners.forEach((listener) => {
    listener(toastState);
  });
}

export function useToast() {
  const [state, setState] = useState<ToastState>(toastState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  const toast = useCallback(
    ({ title, description, variant = 'default', action }: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).substr(2, 9);
      const toastItem: Toast = {
        id,
        title,
        description,
        variant,
        action,
      };
      dispatch(toastItem);
    },
    []
  );

  return {
    toast,
    toasts: state.toasts,
    dismiss,
  };
}